import { Button, Dropdown, Popover } from "@shared/ui";
import type { MenuProps } from "antd";
import { Flex } from "antd";
import { MoreVertical, RotateCw } from "lucide-react";
import type { MouseEvent } from "react";
import styles from "./styles.module.scss";

interface AiMatchAnalyzeButtonProps {
  isAnalyzing: boolean;
  onAnalyze: (event?: MouseEvent) => void;
}

export function AiMatchAnalyzeButton({
  isAnalyzing,
  onAnalyze,
}: AiMatchAnalyzeButtonProps) {
  return (
    <Button
      type="link"
      size="small"
      loading={isAnalyzing}
      className={styles.aiAnalyzeButton}
      onClick={onAnalyze}
    >
      Analyze
    </Button>
  );
}

interface AiMatchFailedActionProps {
  failedReason?: string;
  isAnalyzing: boolean;
  onRerun: (event?: MouseEvent) => void;
  isRetryable: boolean;
}

export function AiMatchFailedAction({
  failedReason,
  isAnalyzing,
  onRerun,
  isRetryable
}: AiMatchFailedActionProps) {
  const popoverContent = (
    <div className={styles.aiFailedPopoverContent}>
      <div className={styles.aiPopoverHeader}>Analysis Failed</div>
      <div className={styles.aiPopoverBody}>
        {failedReason || "Unable to complete AI fit check."}
      </div>
    </div>
  );

  return (
    <Flex align="center" gap={6} className={styles.aiMatchCell}>
      <Popover open={!isRetryable ? undefined : false} content={popoverContent} trigger="hover" placement="right">
      <Button
        type="link"
        size="small"
        loading={isAnalyzing}
        className={styles.aiAnalyzeButton}
        onClick={onRerun}
        disabled={!isRetryable}
      >
        Retry
      </Button>
      </Popover>
      <Popover content={popoverContent} trigger="hover" placement="right">
        <span
          className={styles.aiFailedInfoDot}
          aria-label="View failure reason"
          onClick={(event) => event.stopPropagation()}
        />
      </Popover>
    </Flex>
  );
}

interface AiMatchRerunMenuProps {
  isAnalyzing: boolean;
  onRerun: (event?: MouseEvent) => void;
}

export function AiMatchRerunMenu({
  isAnalyzing,
  onRerun,
}: AiMatchRerunMenuProps) {
  const menuItems: MenuProps["items"] = [
    {
      key: "rerun",
      label: (
        <span className={styles.aiRerunMenuItem}>
          <RotateCw size={13} />
          Rerun
        </span>
      ),
      disabled: isAnalyzing,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onRerun(domEvent as MouseEvent);
      },
    },
  ];

  return (
    <Dropdown trigger={["click", "hover"]} menu={{ items: menuItems }}>
      <Button
        type="text"
        size="small"
        loading={isAnalyzing}
        icon={<MoreVertical size={14} />}
        className={styles.aiMatchMenuButton}
        onClick={(event) => event.stopPropagation()}
      />
    </Dropdown>
  );
}
